import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ScopedSingleLocationFilterMenu } from '@frontend/location-filter-menu';
import { useFormField } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { expandFilterValues, getTimeFrame } from './filters/helpers';
import { useScheduleActivityFilters } from './filters/use-schedule-activity-filters';
import { ScheduleActivityTable } from './schedule-activity-table';
import { useScheduleActivityTracking } from './use-schedule-activity-tracking';

export type PageConfig = { page: number; limit: number };

export const ScheduleActivity = () => {
  const [pageConfig, setPageConfig] = useState<PageConfig>({ page: 1, limit: 50 });

  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();
  const onlineSchedulingReadyLocationIds = getOnlineSchedulingReadyLocationIds();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  // Location dropdown form field
  const selectedLocationField = useFormField(
    {
      type: 'checklist',
      value: onlineSchedulingReadyLocationIds.length ? [onlineSchedulingReadyLocationIds[0]] : [],
    },
    [onlineSchedulingReadyLocationIds]
  );
  const selectedLocationId = selectedLocationField.value[0] ?? '';

  const { filters, filterOptions, setFilters } = useScheduleActivityFilters(selectedLocationId);

  const { data, isLoading, refetch } = SchedulerV3.Queries.useListScheduleActivityQuery({
    request: {
      locationId: selectedLocationId,
      limit: pageConfig.limit,
      page: pageConfig.page,
      activityTypes: filters.activityTypes,
      appointmentTypeIds: filters.appointmentTypeIds,
      activitySourceTypes: expandFilterValues(filters.sourceTypes),
      currentStatuses: expandFilterValues(filters.statusTypes),
      timeFrame: getTimeFrame(filters.startDate, filters.endDate),
    },
    options: {
      enabled: !!selectedLocationId,
    },
  });

  const { trackScheduleActivityViewedEvent, trackFiltersApplied, trackRowClicked } = useScheduleActivityTracking(
    onlineSchedulingReadyLocationIds,
    filterOptions
  );

  // Track schedule activity viewed event
  useEffect(() => {
    if (!onlineSchedulingReadyLocationIds.length) return;
    trackScheduleActivityViewedEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineSchedulingReadyLocationIds.length]);

  return (
    <ScheduleActivityTable
      data={data?.items ?? []}
      totalItems={data?.totalCount ?? 0}
      pageConfig={pageConfig}
      setPageConfig={setPageConfig}
      filterOptions={filterOptions}
      filters={filters}
      setFilters={setFilters}
      isLoading={isLoading}
      showServiceColumn={isIntegratedOffice}
      locationId={selectedLocationId}
      locationDropdown={
        onlineSchedulingReadyLocationIds.length > 1 && (
          <div css={{ marginRight: 'auto' }}>
            <ScopedSingleLocationFilterMenu
              field={selectedLocationField}
              locationIds={onlineSchedulingReadyLocationIds}
              css={locationDropdownStyles}
            />
          </div>
        )
      }
      onRefetch={refetch}
      trackFiltersApplied={trackFiltersApplied}
      trackRowClicked={trackRowClicked}
    />
  );
};

const locationDropdownStyles = css`
  background-color: var(--fabric-button-primary-background-default);
  color: var(--fabric-color-neutral-00);
  p {
    color: var(--fabric-color-neutral-00);
  }
`;
